/** Team — read-only roster from `profiles` (the role registry the database
 * actually enforces). v1's Settings owns invites, removal and the per-user
 * permission editor; this page shows who's in and what their role means. */
import { ROLE_LABELS, type Member } from "./api";

const ROLE_HUE: Record<string, string> = {
  admin: "amber", manager: "blue", acquisitions: "emerald", tc: "violet",
  dispo: "orange", bookkeeper: "sky", va: "slate", member: "slate",
};

export function TeamPage({ members, note }: { members: Member[]; note: string | null }) {
  if (members.length === 0) {
    return (
      <div className="team">
        <div className="empty gate">
          <strong>No team members loaded yet.</strong>
          <span>The roster reads from the shared database — if this stays empty, check the connection banner.</span>
        </div>
      </div>
    );
  }

  const initials = (m: Member) => {
    const src = m.name ?? m.email;
    return src.split(/[\s@.]+/).filter(Boolean).map((x) => x[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
  };

  return (
    <div className="team">
      <header className="bar">
        <span className="count">
          {members.length} team member{members.length === 1 ? "" : "s"}
          {note ? <em className="note"> · {note}</em> : null}
          <em className="note"> · invite, change a role or remove someone in Settings → Users &amp; Roles</em>
        </span>
      </header>
      <div className="teampanel">
        <div className="teampanelhead">👥 Team Members</div>
        <div className="teamlist">
          {members.map((m) => {
            const role = ROLE_LABELS[m.role] ?? { label: m.role, desc: "" };
            const hue = ROLE_HUE[m.role] ?? "slate";
            const active = m.status === "active";
            return (
              <div key={m.email} className="teamrow">
                <span className="cavatar big">{initials(m)}</span>
                <div className="teamwho">
                  <strong>{m.name ?? m.email.split("@")[0]}</strong>
                  <span className="sub">{m.email}</span>
                </div>
                <div className="teamrole">
                  <span className={`rolepill role-${hue}`}>{role.label}</span>
                  {role.desc ? <span className="sub">{role.desc}</span> : null}
                </div>
                <span
                  className={`teamstat ${active ? "teamstat-active" : "teamstat-idle"}`}
                  title={m.status}
                >
                  {active ? "Active" : m.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="sub teamfoot">
        Roles other than Admin/Manager are locked at the database — they can only load the data
        their role allows, even via dev tools.
      </p>
    </div>
  );
}
